import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { formatDate } from '@/lib/dateFormatter';
import { EditIcon } from '@/icons/edit';
import { EyeIcon } from '@/icons/eye';
import { ApiMessage } from '@/services/apiMessage/apiMessage.types';
import type { ColumnDef } from '@tanstack/react-table';

export function getColumns(
  t: (key: string) => string,
  onView: (apiMessageId: string) => void,
  onEdit: (apiMessageId: string) => void,
): ColumnDef<ApiMessage>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => <div className="font-medium">{getSerialNumber(table, row.index)}</div>,
      enableSorting: false,
    },
    {
      accessorKey: 'code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('logsManagement.page.apilogs.form.code')} />
      ),
      cell: ({ row }) => <div>{row.getValue('code')}</div>,
      meta: {
        label: 'Code',
      },
    },
    {
      accessorKey: 'category',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.category')} />,
      cell: ({ row }) => <div>{row.getValue('category')}</div>,
      meta: {
        label: 'Category',
      },
    },
    {
      accessorKey: 'message',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.message')} />,
      cell: ({ row }) => <div className="truncate max-w-[30rem] font-medium">{row.getValue('message')}</div>,
      meta: {
        label: 'Message',
      },
    },
    {
      // Hidden from the table; kept so the toolbar's date filter can target `createdDate`.
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary whitespace-nowrap">
          {formatDate(Number(row.original.createdDate), 'YYYY/MM/DD HH:mm:ss')}
        </div>
      ),
      meta: {
        label: 'Created On',
      },
      enableColumnFilter: true,
      enableHiding: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      cell: ({ row }) => (
        <div className="flex items-center gap-4">
          <Can rule={{ allOf: [PERMS.api.message.detail] }}>
            <button
              type="button"
              aria-label="View"
              className="hover:cursor-pointer"
              onClick={() => onView(row.original.apiMessageId)}
            >
              <EyeIcon color="var(--warning)" />
            </button>
          </Can>
          <Can rule={{ allOf: [PERMS.api.message.detail, PERMS.api.message.update] }}>
            <button
              type="button"
              aria-label="Edit"
              className="hover:cursor-pointer"
              onClick={() => onEdit(row.original.apiMessageId)}
            >
              <EditIcon color="var(--foreground-subtle)" />
            </button>
          </Can>
        </div>
      ),
      size: 80,
      enableSorting: false,
    },
  ];
}
